import React, { useEffect, useRef, useState } from 'react';
import { confirmAction } from '../../utils/confirm';
import {
    createInspectionTemplate, updateInspectionTemplate, deleteInspectionTemplate, InspectionTemplate,
} from '../../services/inspectionsApi';
import TemplateEditor from './TemplateEditor';
import { btn, btnSmall, inGroup } from './common';
import { ui } from '../../styles/shared';

/**
 * Fliken "Mallar": lista över mallar och redigeraren för att skapa/ändra/kopiera.
 * Mallarna kommer från föräldern eftersom fler flikar använder dem.
 */
interface Props {
    group: string;                 // '' = alla grupper
    templates: InspectionTemplate[];
    /** Grupper som redan används, visas som förslag i redigeraren. */
    groups: string[];
    canEdit: boolean;
    /** Laddar om mallar, scheman och räknare i föräldern. */
    reload: () => Promise<void>;
    onError: (msg: string) => void;
}

const TemplatesTab: React.FC<Props> = ({ group, templates, groups, canEdit, reload, onError }) => {
    const [editTemplate, setEditTemplate] = useState<InspectionTemplate | null | 'new'>(null);
    // Ny nyckel varje gång redigeraren öppnas, så att den börjar om med den valda mallen
    // även om en annan mall redan var öppen (den stängs då).
    const [editorKey, setEditorKey] = useState(0);
    const editorRef = useRef<HTMLDivElement>(null);
    const openEditor = (t: InspectionTemplate | 'new') => {
        setEditTemplate(t);
        setEditorKey(k => k + 1);
    };
    // Visa redigeraren när den öppnats. Görs efter ritningen, eftersom en tidigare
    // öppen mall ovanför har stängts och sidan då har flyttat sig.
    useEffect(() => {
        if (editorKey) editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [editorKey]);

    const saveTemplate = async (t: Partial<InspectionTemplate>) => {
        // id 0 = kopia av en mall som ännu inte finns i databasen
        if (editTemplate && editTemplate !== 'new' && editTemplate.id) await updateInspectionTemplate(editTemplate.id, t);
        else await createInspectionTemplate(t);
        setEditTemplate(null);
        await reload();
    };
    /** Öppnar redigeraren med en kopia av mallen; sparas som en ny mall. */
    const cloneTemplate = (t: InspectionTemplate) => {
        openEditor({ ...t, id: 0, name: `${t.name} (kopia)`, schedule_count: 0, version: 1 });
    };
    const removeTemplate = async (t: InspectionTemplate) => {
        if (!confirmAction(`Ta bort mallen "${t.name}"? Gjorda kontroller finns kvar.`)) return;
        try { await deleteInspectionTemplate(t.id); await reload(); } catch (e: any) { onError(e.message); }
    };

    const gTemplates = templates.filter(t => inGroup(group, t.category));

    // En befintlig mall ändras på sin egen plats i listan; ny mall, kopia och en mall
    // som inte syns i vald grupp visas överst.
    const editingId = editTemplate && editTemplate !== 'new' && gTemplates.some(t => t.id === editTemplate.id) ? editTemplate.id : 0;
    const editor = editTemplate && (
        <div ref={editorRef} style={{ scrollMarginTop: 12 }}>
            <TemplateEditor key={editorKey} template={editTemplate === 'new' ? null : editTemplate} groups={groups} onSave={saveTemplate} onCancel={() => setEditTemplate(null)} />
        </div>
    );

    return (
        <div>
            {!editingId && editor}
            {canEdit && !editTemplate && (
                <div style={{ marginBottom: 14 }}>
                    <button onClick={() => openEditor('new')} style={btn('#28a745')}>+ Ny mall</button>
                </div>
            )}
            {gTemplates.length === 0 ? (
                <div style={{ ...ui.card, color: '#666' }}>Inga mallar ännu. En mall är listan med det som ska kontrolleras, till exempel för brandskydd.</div>
            ) : gTemplates.map(t => t.id === editingId ? (
                <React.Fragment key={t.id}>{editor}</React.Fragment>
            ) : (
                <div key={t.id} style={{ ...ui.card, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', opacity: t.active ? 1 : 0.6 }}>
                    <div>
                        <div style={{ fontWeight: 'bold', fontSize: 15 }}>{t.name} <span style={{ fontSize: 12, fontWeight: 'normal', background: '#eee', borderRadius: 10, padding: '2px 8px', marginLeft: 6 }}>{t.category}</span>{!t.active && <span style={{ fontSize: 12, color: '#777', marginLeft: 6 }}>inaktiv</span>}</div>
                        {t.description && <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>{t.description}</div>}
                        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                            {t.fields.filter(f => f.type !== 'heading').length} punkter · version {t.version} · används i {t.schedule_count ?? 0} schema{(t.schedule_count ?? 0) === 1 ? '' : 'n'}
                            {(t.notify_done_group_name || t.notify_done_name) && <> · meddelar {t.notify_done_group_name ? `${t.notify_done_group_name} (grupp)` : t.notify_done_name} vid klart</>}
                        </div>
                    </div>
                    {canEdit && (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                            <button onClick={() => openEditor(t)} style={btnSmall('#e8f0fe', '#1a4aa6')}>Ändra</button>
                            <button onClick={() => cloneTemplate(t)} style={btnSmall('#eee', '#333')} title="Skapa en ny mall med samma fält">Kopiera</button>
                            <button onClick={() => removeTemplate(t)} style={btnSmall('#f8d7da', '#721c24')}>Ta bort</button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default TemplatesTab;
